import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { useAnswerContext } from '../../context';
import {
  Button,
  Divider,
  Link,
  makeStyles,
  Paper,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import Lottie from 'lottie-react';
import { introCentered } from '../../lottie';
import Tran from '../../lottie/_Transitioning.json';
import Inno from '../../lottie/_Innovating.json';
import Opt from '../../lottie/_Optimizing.json';
import Plan from '../../lottie/_Planning.json';

const getSrc = (sum: number) => {
  if (sum === 1) {
    return Plan;
  }
  if (sum === 2) {
    return Tran;
  }
  if (sum === 3) {
    return Opt;
  }
  return Inno;
};

export const getText = (sum: number) => {
  if (sum === 1) {
    return 'Planning';
  }
  if (sum === 2) {
    return 'Transitioning';
  }
  if (sum === 3) {
    return 'Optimizing';
  }
  return 'Innovating';
};

const styles = [
  {
    color: '#E37400',
    background: '#FDD663',
  },
  {
    color: '#1A73E8',
    background: '#D2E3FC',
  },
  {
    color: 'rgb(234, 67, 53)',
    background: 'rgb(250, 210, 207)',
  },
  {
    color: '#34A853',
    background: '#CEEAD6',
  },
];

const getYouAreHere = (num: number, summary: number, className: string) => {
  return summary === num ? (
    <div className={className} style={styles[num - 1] as any}>
      You are here
    </div>
  ) : (
    <div></div>
  );
};

const getColor = (num: number | null, summary: number) => {
  if (num === null) {
    return styles[summary - 1].color;
  }
  return num === summary ? styles[num - 1].color : 'undefined';
};

const useStyles = makeStyles((theme) => ({
  root: {
    margin: '30px',
    marginTop: 93,
    display: 'flex',
    flexDirection: 'column',
    width: 900,
    maxWidth: '90%',
    zIndex: 2,
  },
  paper: {
    maxWidth: 900,
    borderRadius: 15,
    boxShadow: 'rgb(0 0 0 / 10%) 0px 2px 13px 0px',
    position: 'relative',
  },
  accordin: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    alignContent: 'center',
    margin: '0 auto',
    alignSelf: 'stretch',
  },
  images: {
    marginTop: 70,
    marginBottom: 20,
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '10px 10px',
  },
  coloredLine: {
    position: 'absolute',
    top: 0,
    margin: 'auto',
    height: 5,
    width: 150,
    left: 'calc(50% - 75px)',
    borderBottomLeftRadius: 5,
    borderBottomRightRadius: 5,
  },
  image: {
    [theme.breakpoints.down('sm')]: {
      width: 250,
    },
    [theme.breakpoints.up('sm')]: {
      width: 190,
    },
  },
  text: {
    fontSize: '18px',
    lineHeight: '30px',
    letterSpacing: '0.2px',
    textAlign: 'center',
  },
  retake: {
    padding: '12px 24px',
    marginLeft: 30,
    boxShadow: 'none',
    background: '#fff',
    border: '1px solid #dadce0',
    color: '#1a73e8',
    fontSize: 16,
    fontWeight: 500,
    outline: 'none',
    '&:hover': {
      background: '#f6f9fe',
      borderColor: '#1a73e8',
      color: '#174ea6',
      boxShadow: 'none',
      outline: 'none',
    },
    '&:focus': {
      background: '#e4eefc',
      borderColor: '#1a73e8',
      boxShadow: '0 0 0 1px #1a73e8 !important',
      color: '#174ea6',
      outline: 'none',
    },
    '&:active': {
      background: '#e8f1fd',
      borderColor: '#1a73e8',
      boxShadow: 'none !important',
      color: '#174ea6',
      outline: 'none',
    },
  },
  logo: {
    width: 200,
    position: 'relative',
    zIndex: 10,
    marginLeft: '-18px',
    alignSelf: 'flex-start',
  },
  accordinMobile: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  headerStay: {
    zIndex: 2,
    position: 'absolute',
    alignSelf: 'center',
    [theme.breakpoints.down('xs')]: {
      top: 80,
      width: 400,
    },
    [theme.breakpoints.up('xs')]: {
      width: 500,
      top: 60,
    },
    [theme.breakpoints.up('sm')]: {
      width: 500,
      top: 70,
    },
    [theme.breakpoints.up('md')]: {
      top: 40,
      width: 700,
    },
  },
  paragraph: {
    maxWidth: 510,
    alignSelf: 'center',
    textAlign: 'center',
    fontSize: '18px',
    lineHeight: '30px',
    marginTop: 30,
    letterSpacing: '0.2px',
    position: 'relative',
    zIndex: 10,
  },
  backdrop: {
    background: '#F1F3F4',
    width: '100%',
    position: 'absolute',
    zIndex: 1,
    height: 500,
    left: 0,
    top: 920,
    [theme.breakpoints.down('xs')]: {
      height: 680,
    },
    [theme.breakpoints.between('xs', 'sm')]: {
      top: 860,
      height: 670,
    },
    [theme.breakpoints.between('sm', 'md')]: {
      height: 500,
    },
    [theme.breakpoints.between('md', 'lg')]: {
      height: 500,
    },
    [theme.breakpoints.up('lg')]: {
      height: 500,
    },
  },
  desc: {
    textAlign: 'center',
    fontSize: 18,
    lineHeight: '38px',
    fontWeight: 500,
    letterSpacing: '0.2px',
  },
  headerLogo: {
    height: 210,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    position: 'relative',
    margin: '0 auto',
    marginTop: 80,
    overflow: 'hidden',
  },
  here: {
    textAlign: 'center',
    fontFamily: '"Google Sans", Roboto, sans-serif',
    fontSize: 12,
    lineHeight: '12px',
    fontWeight: 500,
    letterSpacing: '0.06px',
    padding: '6px 20px',
    borderRadius: '100px',
    justifySelf: 'center',
    width: 110,
  },
  paperText1: {
    margin: '50px 60px',
    color: '#202124',
    [theme.breakpoints.down('xs')]: {
      margin: '50px 20px',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '24px',
      lineHeight: '32px',
      textAlign: 'center',
    },
    [theme.breakpoints.up('sm')]: {
      fontSize: '28px',
      lineHeight: '46px',
      letterSpacing: '0.3px',
    },
  },
  paperText2: {
    margin: '50px 60px',
    color: '#5F6368',
    [theme.breakpoints.down('sm')]: {
      fontSize: '23px',
      lineHeight: '30px',
      letterSpacing: '0.2px',
      textAlign: 'center',
    },
    [theme.breakpoints.up('sm')]: {
      fontSize: '24px',
      lineHeight: '38px',
      letterSpacing: '0.2px',
    },
    [theme.breakpoints.down('xs')]: {
      margin: '50px 20px',
    },
    [theme.breakpoints.up('md')]: {
      width: '60%',
    },
  },
}));

const resultText = [
  'You see there are new ways of working with data and are considering how you can lead your organization through change.',
  'You are actively exploring solutions to drive value from data securely, reliably and quickly',
  "While you're ahead of the curve by quickly adapting new ways of driving value from data—there is room to optimize.",
  "You're leading the pack in terms of how you drive value from data—with secure, intelligent and flexible tools in place.",
];

export const Result = () => {
  const {
    accordin,
    images,
    image,
    headerStay,
    logo,
    root,
    here,
    paper,
    headerLogo,
    desc,
    retake,
    paperText1,
    paperText2,
    coloredLine,
    backdrop,
    paragraph,
    accordinMobile,
  } = useStyles();
  const { values, retake: retakeHandler } = useAnswerContext();
  const imagesRef = useRef<HTMLDivElement>(null);
  const history = useHistory<any>();

  useEffect(() => {
    if (!history.location.state?.complete) {
      const search = history.location.search;
      history.push(
        history.location.pathname
          ? history.location.pathname + search
          : '/' + search,
        { complete: true },
      );
    }
  }, [history]);

  useEffect(() => {
    const unlisten = history.listen((x: any) => {
      if (x.state?.activeStep) {
        retakeHandler('7');
      }
    });
    return () => {
      unlisten();
    };
  }, [history, retakeHandler]);

  let summary =
    Math.floor(
      Object.values(values).reduce(
        (sum: number, current) =>
          typeof current === 'number' ? sum + current : sum + current[0],
        0,
      ) / 10,
    ) + 1;
  summary = Math.min(summary, 4);

  useLayoutEffect(() => {
    if (!imagesRef.current) {
      return;
    }
    imagesRef.current.scrollIntoView();
  }, []);
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('sm'));
  const paperRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <div className={backdrop}></div>
      <div className={root}>
        <div>
          <div className={headerLogo}>
            <img
              className={logo}
              src={process.env.PUBLIC_URL + '/images/google_cloud_logo.png'}
              alt="google cloud logo"
            />
            <Lottie className={headerStay} animationData={introCentered} />
          </div>
        </div>
        <p className={paragraph}>
          Whether you're planning, transitioning, optimizing or innovating,
          Google Cloud helps you take your next best step.
        </p>
        {matches ? (
          <div className={accordinMobile}>
            <Lottie className={image} animationData={getSrc(summary)} />
            {getYouAreHere(1, summary, here)}
            {getYouAreHere(2, summary, here)}
            {getYouAreHere(3, summary, here)}
            {getYouAreHere(4, summary, here)}
            <div className={desc} style={{ color: getColor(null, summary) }}>
              {getText(summary)}
            </div>
          </div>
        ) : (
          <div className={accordin}>
            <div className={images} ref={imagesRef}>
              {summary === 1 ? (
                <Lottie className={image} animationData={getSrc(summary)} />
              ) : (
                <img className={image} src={process.env.PUBLIC_URL + "/images/form1.png"} alt="figure 1" />
              )}
              {summary === 2 ? (
                <Lottie className={image} animationData={getSrc(summary)} />
              ) : (
                <img className={image} src={process.env.PUBLIC_URL + "/images/form2.png"} alt="figure 1" />
              )}
              {summary === 3 ? (
                <Lottie className={image} animationData={getSrc(summary)} />
              ) : (
                <img className={image} src={process.env.PUBLIC_URL + "/images/form3.png"} alt="figure 1" />
              )}
              {summary === 4 ? (
                <Lottie className={image} animationData={getSrc(summary)} />
              ) : (
                <img className={image} src={process.env.PUBLIC_URL + "/images/form4.png"} alt="figure 1" />
              )}
              {getYouAreHere(1, summary, here)}
              {getYouAreHere(2, summary, here)}
              {getYouAreHere(3, summary, here)}
              {getYouAreHere(4, summary, here)}
              <div className={desc} style={{ color: getColor(1, summary) }}>
                Planning
              </div>
              <div className={desc} style={{ color: getColor(2, summary) }}>
                Transitioning
              </div>
              <div className={desc} style={{ color: getColor(3, summary) }}>
                Optimizing
              </div>
              <div className={desc} style={{ color: getColor(4, summary) }}>
                Innovating
              </div>
            </div>
          </div>
        )}
        <Paper className={paper} ref={paperRef}>
          {matches ? (
            <div
              className={coloredLine}
              style={{ background: getColor(null, summary) }}
            ></div>
          ) : (
            <div
              className={coloredLine}
              style={{
                background: getColor(null, summary),
                transform: `translateX(${
                  summary === 1
                    ? -300
                    : summary === 2
                    ? -100
                    : summary === 3
                    ? 105
                    : 300
                }px)`,
              }}
            ></div>
          )}
          <p className={paperText1}>{resultText[summary - 1]}</p>
          <Divider />
          <p className={paperText2}>
            <strong>Download our guide</strong> to learn about the steps your
            organization can take to accelerate your journey towards a
            data-driven culture.
          </p>
          <div
            className={paperText1}
            style={{
              display: 'flex',
              justifyContent: matches ? 'center' : 'flex-start',
            }}
          >
            <Link
              target="blank"
              underline="none"
              href="https://services.google.com/fh/files/misc/building_a_data_driven_culture_guide_2020_google.pdf"
              download="building_a_data_driven_culture_guide_2020_google.pdf"
            >
              <Button color="primary" variant="contained">
                {matches ? 'Download' : 'Download guide'}
              </Button>
            </Link>

            <Button
              color="primary"
              className={retake}
              onClick={() => retakeHandler()}
            >
              {matches ? 'Retake' : 'Retake assessment'}
            </Button>
          </div>
        </Paper>
      </div>
    </>
  );
};
